import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { XIcon } from "lucide-react";
import { cn } from "src/shadcn/lib/utils";
import { Button } from "src/shadcn/components/button";

import {
  Dialog as ShadcnDialog,
  DialogClose as ShadcnDialogClose,
  DialogDescription as ShadcnDialogDescription,
  DialogFooter as ShadcnDialogFooter,
  DialogHeader as ShadcnDialogHeader,
  DialogOverlay as ShadcnDialogOverlay,
  DialogPortal as ShadcnDialogPortal,
  DialogTitle as ShadcnDialogTitle,
  DialogTrigger as ShadcnDialogTrigger,
} from "src/shadcn/components/dialog";

const Dialog = (props: React.ComponentProps<typeof ShadcnDialog>) => (
  <ShadcnDialog {...props} />
);

const DialogClose = (props: React.ComponentProps<typeof ShadcnDialogClose>) => (
  <ShadcnDialogClose {...props} />
);

/**
 * DialogContent composes Portal + Overlay + Radix Content so that
 * `overlayClassName` can be forwarded to the overlay element.
 */
const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  overlayClassName?: string;
}) => (
  <ShadcnDialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      data-slot="dialog-content"
      className={cn(
        "fixed top-1/2 start-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 gap-5 rounded-xl bg-popover p-6 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close data-slot="dialog-close" asChild>
          <Button
            variant="ghost"
            className="absolute top-2 end-2"
            size="icon-sm"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </ShadcnDialogPortal>
);

const DialogDescription = (
  props: React.ComponentProps<typeof ShadcnDialogDescription>
) => <ShadcnDialogDescription {...props} />;

const DialogFooter = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnDialogFooter>) => (
  <ShadcnDialogFooter
    className={cn("-mx-6 -mb-6 border-t-0 px-6 py-4", className)}
    {...props}
  />
);

const DialogHeader = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnDialogHeader>) => (
  <ShadcnDialogHeader className={cn("gap-3", className)} {...props} />
);

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnDialogOverlay>) => (
  <ShadcnDialogOverlay
    className={cn("supports-backdrop-filter:backdrop-blur-none", className)}
    {...props}
  />
);

const DialogPortal = (
  props: React.ComponentProps<typeof ShadcnDialogPortal>
) => <ShadcnDialogPortal {...props} />;

const DialogTitle = (props: React.ComponentProps<typeof ShadcnDialogTitle>) => (
  <ShadcnDialogTitle {...props} />
);

const DialogTrigger = (
  props: React.ComponentProps<typeof ShadcnDialogTrigger>
) => <ShadcnDialogTrigger {...props} />;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
